import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  Clock,
  CheckCircle,
  XCircle,
  DollarSign,
  Download,
  Eye,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPayouts() {
  const { getAuthHeaders } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    status: "",
    admin_notes: "",
    payment_reference: "",
  });

  useEffect(() => {
    fetchPayouts();
  }, []);

  const fetchPayouts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/payouts`, {
        credentials: "include",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        setPayouts(await response.json());
      }
    } catch (error) {
      toast.error("Failed to load payouts");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePayout = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/admin/payouts/${selectedPayout.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateForm),
      });

      if (response.ok) {
        toast.success("Payout updated!");
        setDialogOpen(false);
        setSelectedPayout(null);
        fetchPayouts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update payout");
      }
    } catch (error) {
      toast.error("Failed to update payout");
    }
  };

  const openUpdateDialog = (payout) => {
    setSelectedPayout(payout);
    setUpdateForm({
      status: payout.status,
      admin_notes: payout.admin_notes || "",
      payment_reference: payout.payment_reference || "",
    });
    setDialogOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "paid":
        return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case "rejected":
        return <XCircle className="w-4 h-4 text-red-400" />;
      case "approved":
      case "processing":
        return <Clock className="w-4 h-4 text-blue-400" />;
      default:
        return <Clock className="w-4 h-4 text-amber-400" />;
    }
  };

  const filteredPayouts = payouts.filter(
    (p) => statusFilter === "all" || p.status === statusFilter
  );

  const statuses = [...new Set(payouts.map((p) => p.status))];

  const pendingTotal = payouts
    .filter((p) => p.status === "requested")
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  if (loading) {
    return (
      <DashboardLayout title="Payouts" subtitle="Manage payout requests">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Payouts" subtitle="Manage payout requests">
      <div data-testid="admin-payouts" className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Pending</span>
            </div>
            <p className="text-2xl font-bold text-amber-400">
              {payouts.filter((p) => p.status === "requested").length}
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm">Pending Value</span>
            </div>
            <p className="text-2xl font-bold text-amber-400">{formatCurrency(pendingTotal)}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Paid</span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">
              {payouts.filter((p) => p.status === "paid").length}
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm">Total Paid</span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">
              {formatCurrency(
                payouts.filter((p) => p.status === "paid").reduce((sum, p) => sum + (p.amount || 0), 0)
              )}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] bg-card border-border text-foreground">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all">All Status</SelectItem>
              {statuses.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Affiliate</th>
                  <th>Requested</th>
                  <th>Period</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayouts.length > 0 ? (
                  filteredPayouts.map((payout) => (
                    <tr key={payout.id}>
                      <td>
                        <div>
                          <p className="font-medium text-foreground">{payout.affiliate_name}</p>
                          <p className="text-xs text-slate-500">{payout.affiliate_email}</p>
                        </div>
                      </td>
                      <td className="text-muted-foreground">{formatDate(payout.requested_at)}</td>
                      <td className="text-muted-foreground">
                        {formatDate(payout.period_start)} - {formatDate(payout.period_end)}
                      </td>
                      <td className="text-foreground font-medium">{formatCurrency(payout.amount)}</td>
                      <td>
                        <div className="text-muted-foreground">
                          <p className="capitalize">{payout.payout_method || "-"}</p>
                          {payout.wallet_address && (
                            <p className="text-xs text-slate-500 font-mono">
                              {payout.wallet_address.slice(0, 10)}...
                            </p>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(payout.status)}
                          <span
                            className={`capitalize ${
                              payout.status === "paid"
                                ? "text-emerald-400"
                                : payout.status === "rejected"
                                ? "text-red-400"
                                : payout.status === "approved" || payout.status === "processing"
                                ? "text-blue-400"
                                : "text-amber-400"
                            }`}
                          >
                            {payout.status}
                          </span>
                        </div>
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openUpdateDialog(payout)}
                          data-testid={`update-payout-${payout.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> Review
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-slate-500 py-8">
                      {statusFilter !== "all" ? "No payouts match your filter" : "No payout requests yet"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Update Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground">Review Payout</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdatePayout} className="space-y-4 mt-4">
              <div className="p-4 rounded-lg bg-background border border-border space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Affiliate</span>
                  <span className="text-foreground">{selectedPayout?.affiliate_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="text-foreground font-bold">{formatCurrency(selectedPayout?.amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Period</span>
                  <span className="text-foreground">
                    {formatDate(selectedPayout?.period_start)} - {formatDate(selectedPayout?.period_end)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Method</span>
                  <span className="text-foreground capitalize">{selectedPayout?.payout_method || "Not set"}</span>
                </div>
                {selectedPayout?.wallet_address && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Wallet</span>
                    <span className="text-foreground font-mono text-sm">{selectedPayout.wallet_address}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Status</Label>
                <Select
                  value={updateForm.status}
                  onValueChange={(value) => setUpdateForm({ ...updateForm, status: value })}
                >
                  <SelectTrigger className="bg-background border-border text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="requested">Requested</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Payment Reference</Label>
                <Input
                  value={updateForm.payment_reference}
                  onChange={(e) => setUpdateForm({ ...updateForm, payment_reference: e.target.value })}
                  placeholder="Transaction ID, TXID, etc."
                  className="bg-background border-border text-foreground"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Admin Notes</Label>
                <Textarea
                  value={updateForm.admin_notes}
                  onChange={(e) => setUpdateForm({ ...updateForm, admin_notes: e.target.value })}
                  placeholder="Internal notes about this payout"
                  className="bg-background border-border text-foreground"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 glow-primary">
                  Update Payout
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
